import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "register-page" }
const _hoisted_2 = { class: "page-header" }
const _hoisted_3 = { class: "page-content" }
const _hoisted_4 = { class: "tab-header" }
const _hoisted_5 = { class: "title" }
const _hoisted_6 = { class: "actions" }
const _hoisted_7 = { class: "tab-content" }
const _hoisted_8 = { class: "flex justify-center" }

import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter, useRoute } from "vue-router";
import store from "@/store";

import { toast } from "@/common/utils/toast";

import { franchiseGet, franchisePatch } from "@/common/api/rest/franchise";
import { avatarAdd, avatarPut, avatarDel } from "@/common/api/rest/avatar";

import type { TranslationFieldsType } from "@/common/types/language";
import type { FranchiseType } from "@/common/types/franchise";

import { FormFranchise, ListTranslate } from "@/components";
import {
  baseAvatar,
  breadcrumbSimple,
  baseConfirm,
  svgIcon,
} from "@/common/components";


export default /*@__PURE__*/_defineComponent({
  __name: 'edit',
  setup(__props) {

const { t, locale } = useI18n();
const router = useRouter();
const route = useRoute();

const translationFields = ref<TranslationFieldsType[]>([
  {
    type: "input",
    field: "name",
    label: "common.labels.name",
    value: "",
  },
  {
    type: "textarea",
    field: "description",
    label: "common.labels.description",
    value: "",
  },
]);

const franchiseData = ref<FranchiseType | null>(null);

const showAvatarDel = ref(false);

const franchiseId = computed(() => route.params.franchise as string);

const breadcrumbList = computed(() => [
  {
    key: "register",
    label: t("common.labels.registrations"),
    url: "/register",
  },
  {
    key: "franchise",
    label: t("franchise.labels.franchises"),
    url: `/register/franchise`,
  },
  {
    key: "edit",
    label: franchiseData.value?.name ?? "...",
  },
]);

function back() {
  router.push("/register/franchise");
}

// franchise
function fetchData() {
  franchiseGet(franchiseId.value)
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        franchiseData.value = data;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function patchData(payload: Partial<FranchiseType>) {
  franchisePatch(franchiseId.value, payload)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchData();
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

// AVATAR
function addAvatar(avatar: string) {
  if (!store.getters.user) return;

  store.commit("SET_LOADING", true);

  avatarAdd(franchiseData.value.franchise_id, avatar)
    .then((response) => {
      const { success, message } = response;

      fetchData();
      toast({ message: message, type: success ? "success" : "error" });
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

function updateAvatar(avatar: string) {
  if (!franchiseData.value.avatar) return;

  store.commit("SET_LOADING", true);

  avatarPut(franchiseData.value.franchise_id, avatar)
    .then((response) => {
      const { success, message } = response;

      fetchData();
      toast({ message: message, type: success ? "success" : "error" });
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

function changeAvatar(avatar: string) {
  if (franchiseData.value.avatar) {
    updateAvatar(avatar);
  } else {
    addAvatar(avatar);
  }
}

function delAvatar() {
  showAvatarDel.value = true;
}

function delAvatarConfirm() {
  if (!franchiseData.value?.avatar) return;

  store.commit("SET_LOADING", true);

  avatarDel(franchiseData.value.franchise_id)
    .then((response) => {
      const { success, message } = response;

      fetchData();

      toast({ message: message, type: success ? "success" : "error" });
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      showAvatarDel.value = false;
      store.commit("SET_LOADING", false);
    });
}

onMounted(() => {
  fetchData();
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("article", _hoisted_1, [
    _createElementVNode("section", _hoisted_2, [
      _createVNode(_unref(breadcrumbSimple), {
        items: breadcrumbList.value,
        "show-home": true,
        "url-home": "/home",
        active: "edit"
      }, null, 8, ["items"])
    ]),
    _createElementVNode("section", _hoisted_3, [
      _createElementVNode("div", _hoisted_4, [
        _createElementVNode("div", _hoisted_5, [
          _createVNode(_unref(svgIcon), {
            icon: "no-image",
            size: "2rem"
          }),
          _createTextVNode(" " + _toDisplayString(_unref(t)("franchise.labels.edit_franchise")), 1)
        ]),
        _createElementVNode("div", _hoisted_6, [
          _createElementVNode("button", {
            class: "btn btn-default",
            onClick: _cache[0] || (_cache[0] = ($event: any) => (back()))
          }, [
            _createVNode(_unref(svgIcon), {
              icon: "close",
              size: "1rem"
            })
          ])
        ])
      ]),
      _createElementVNode("div", _hoisted_7, [
        _createElementVNode("div", _hoisted_8, [
          _createVNode(_unref(baseAvatar), {
            position: "center",
            "is-edit": true,
            "show-image": true,
            url: franchiseData.value?.avatar?.large,
            "aspect-ratio": 1.333,
            "border-radius": "0.25rem",
            onChange: _cache[1] || (_cache[1] = ($event: any) => (changeAvatar($event))),
            onDel: _cache[2] || (_cache[2] = ($event: any) => (delAvatar()))
          }, null, 8, ["url"])
        ]),
        _createVNode(_unref(FormFranchise), {
          data: franchiseData.value,
          "is-patch": true,
          "onUpdate:data": _cache[3] || (_cache[3] = ($event: any) => (patchData($event)))
        }, null, 8, ["data"]),
        _createVNode(_unref(ListTranslate), {
          "owner-id": franchiseId.value,
          language: _unref(locale).toLowerCase(),
          fields: translationFields.value
        }, null, 8, ["owner-id", "language", "fields"])
      ])
    ]),
    _createVNode(_unref(baseConfirm), {
      show: showAvatarDel.value,
      title: _unref(t)('common.modals.titles.remove_image').toUpperCase(),
      message: _unref(t)('common.modals.messages.remove_image'),
      onCancel: _cache[4] || (_cache[4] = ($event: any) => (showAvatarDel.value = false)),
      onConfirm: _cache[5] || (_cache[5] = ($event: any) => (delAvatarConfirm()))
    }, null, 8, ["show", "title", "message"])
  ]))
}
}

})